import { generateClient } from "aws-amplify/api";
import "../amplify"; // Import Amplify configuration
import {
  createLeaguePlayerStats,
  updateLeaguePlayerStats,
  deleteLeaguePlayerStats,
} from "../../graphql/mutations";
import { listLeaguePlayerStats } from "../../graphql/queries";

// Initialize the API client with explicit API key authentication
const client = generateClient({
  authMode: "apiKey",
});

export interface CalculatedStanding {
  userId?: string;
  playerConstructId?: string;
  totalPoints: number;
  totalEvents: number;
  bestRank: number;
  averageRank: number;
  averageFinishPercentile: number;
  currentRank: number;
  totalWins: number;
  totalLosses: number;
  totalDraws: number;
}

/**
 * Save calculated standings for a league to the database
 */
export async function saveLeagueStandings(
  leagueId: string,
  standings: CalculatedStanding[]
): Promise<void> {
  try {
    console.log(`Saving ${standings.length} standings for league ${leagueId}`);

    // First, get existing standings for this league
    const existingResult = await client.graphql({
      query: listLeaguePlayerStats,
      variables: {
        filter: { leagueId: { eq: leagueId } },
        limit: 1000,
      },
    });

    const existingStats = existingResult.data.listLeaguePlayerStats.items;
    console.log(
      `Found ${existingStats.length} existing stats for league ${leagueId}`
    );

    // Create a map of existing stats by playerConstructId
    const existingStatsMap = new Map<string, any>();
    existingStats.forEach((stat: any) => {
      if (stat.playerConstructId) {
        existingStatsMap.set(stat.playerConstructId, stat);
      }
    });

    // Process each standing
    for (const standing of standings) {
      if (standing.playerConstructId) {
        const existingStat = existingStatsMap.get(standing.playerConstructId);

        if (existingStat) {
          // Update existing stat
          await client.graphql({
            query: updateLeaguePlayerStats,
            variables: {
              input: {
                id: existingStat.id,
                totalPoints: standing.totalPoints,
                totalEvents: standing.totalEvents,
                totalWins: standing.totalWins,
                totalLosses: standing.totalLosses,
                totalDraws: standing.totalDraws,
                bestRank: standing.bestRank,
                averageRank: standing.averageRank,
                averageFinishPercentile:
                  standing.averageFinishPercentile,
                currentRank: standing.currentRank,
              },
            },
          });
          console.log(
            `Updated stats for player construct ${standing.playerConstructId}`
          );
        } else {
          // Create new stat
          await client.graphql({
            query: createLeaguePlayerStats,
            variables: {
              input: {
                leagueId: leagueId,
                playerConstructId: standing.playerConstructId,
                totalPoints: standing.totalPoints,
                totalEvents: standing.totalEvents,
                totalWins: standing.totalWins,
                totalLosses: standing.totalLosses,
                totalDraws: standing.totalDraws,
                bestRank: standing.bestRank,
                averageRank: standing.averageRank,
                averageFinishPercentile:
                  standing.averageFinishPercentile,
                currentRank: standing.currentRank,
              },
            },
          });
          console.log(
            `Created new stats for player construct ${standing.playerConstructId}`
          );
        }
      }
    }

    // Remove stats for players who are no longer in the standings
    const currentPlayerConstructIds = standings
      .map((s) => s.playerConstructId)
      .filter(Boolean);

    for (const existingStat of existingStats) {
      if (
        existingStat.playerConstructId &&
        !currentPlayerConstructIds.includes(existingStat.playerConstructId)
      ) {
        await client.graphql({
          query: deleteLeaguePlayerStats,
          variables: {
            input: { id: existingStat.id },
          },
        });
        console.log(
          `Deleted stats for removed player construct ${existingStat.playerConstructId}`
        );
      }
    }

    console.log(`Successfully saved standings for league ${leagueId}`);
  } catch (error) {
    console.error("Error saving league standings:", error);
    throw error;
  }
}

/**
 * Get stored standings for a league
 */
export async function getLeagueStandings(leagueId: string): Promise<any[]> {
  try {
    console.log(`Fetching stored standings for league ${leagueId}`);

    const result = await client.graphql({
      query: listLeaguePlayerStats,
      variables: {
        filter: { leagueId: { eq: leagueId } },
        limit: 1000,
      },
    });

    const stats = result.data.listLeaguePlayerStats.items;
    console.log(`Found ${stats.length} stored stats for league ${leagueId}`);

    return stats;
  } catch (error) {
    console.error("Error fetching league standings:", error);
    throw error;
  }
}
